// Global search: Cmd/Ctrl+K (or the Search button) opens a dialog. Pages come from the sidebar's own links
// (already filtered to what this person can open); records come from GET /search as JSON.
// Without JavaScript the Search button is a plain link to the /search page.

interface Hit { title: string; sub: string; href: string }
interface Group { key: string; label: string; items: Hit[] }

const found = document.querySelector<HTMLDialogElement>('dialog.search-dialog');
if (found) {
  const dlg = found;
  const input = dlg.querySelector<HTMLInputElement>('#search-input')!;
  const list = dlg.querySelector<HTMLElement>('#search-list')!;
  const status = dlg.querySelector<HTMLElement>('.search-status')!;
  const all = dlg.querySelector<HTMLAnchorElement>('.search-all')!;
  const inOrg = Boolean(document.body.dataset.org);
  const mac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  let opener: HTMLElement | null = null;
  let records: Group[] = [];
  let loading = false;
  let seq = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let ctl: AbortController | undefined;
  let active = -1;

  document.querySelectorAll<HTMLElement>('[data-search-hint]').forEach(k => { k.textContent = mac ? '⌘K' : 'Ctrl K'; k.parentElement?.setAttribute('title', `Search (${mac ? '⌘K' : 'Ctrl K'})`); });

  const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

  /** The sidebar's links (by category) and the account menu's, read when the dialog opens so they are always current. */
  function pages(): Group {
    const items: Hit[] = [...document.querySelectorAll<HTMLAnchorElement>('.sb-nav a.sb-item')].map(a => ({
      title: a.querySelector('.sb-label')?.textContent?.trim() || a.title, sub: a.closest('nav')?.getAttribute('aria-label') ?? '', href: a.getAttribute('href')!,
    }));
    for (const [href, title] of [['/account', 'Account settings'], ['/account/security', 'Sign-in security'], ['/support', 'Help and support'], ['/help', 'Help centre']] as const) {
      if (document.querySelector(`.sidebar a[href="${href}"]`)) items.push({ title, sub: 'Account', href });
    }
    return { key: 'pages', label: 'Pages', items };
  }

  const options = () => [...list.querySelectorAll<HTMLAnchorElement>('[role=option]')];
  function setActive(i: number, scroll = true) {
    const o = options();
    o[active]?.setAttribute('aria-selected', 'false');
    active = o.length ? (i + o.length) % o.length : -1;
    const cur = o[active];
    cur?.setAttribute('aria-selected', 'true');
    if (cur) input.setAttribute('aria-activedescendant', cur.id); else input.removeAttribute('aria-activedescendant');
    if (scroll) cur?.scrollIntoView({ block: 'nearest' });
  }

  function render() {
    const q = input.value.trim();
    const needle = fold(q);
    const p = pages();
    const shown: Group[] = [{ ...p, items: p.items.filter(i => !needle || fold(i.title).includes(needle)) }, ...(q.length >= 2 ? records : [])].filter(g => g.items.length);
    list.replaceChildren();
    let n = 0;
    for (const g of shown) {
      const grp = document.createElement('div');
      grp.setAttribute('role', 'group');
      const h = document.createElement('div');
      h.className = 'sb-head'; h.id = `search-h-${g.key}`; h.textContent = g.label;
      grp.setAttribute('aria-labelledby', h.id);
      grp.append(h);
      for (const it of g.items) {
        const a = document.createElement('a');
        a.className = 'search-opt'; a.id = `search-o-${n++}`; a.href = it.href; a.setAttribute('role', 'option'); a.setAttribute('aria-selected', 'false');
        const t = document.createElement('span'); t.className = 'search-t'; t.textContent = it.title;
        a.append(t);
        if (it.sub) { const s = document.createElement('span'); s.className = 'search-s'; s.textContent = it.sub; a.append(s); }
        grp.append(a);
      }
      list.append(grp);
    }
    active = -1;
    setActive(0, false);
    status.textContent = loading ? 'Searching…' : !shown.length ? `Nothing matches “${q}”.` : '';
    all.hidden = !inOrg || q.length < 2;
    all.href = `/search?q=${encodeURIComponent(q)}`;
  }

  async function fetchRecords() {
    ctl?.abort();
    const q = input.value.trim();
    if (!inOrg || q.length < 2) { records = []; loading = false; return render(); }
    const mine = ++seq;
    ctl = new AbortController();
    loading = true; render();
    try {
      const r = await fetch(`/search?q=${encodeURIComponent(q)}`, { headers: { Accept: 'application/json' }, signal: ctl.signal });
      if (!r.ok) throw new Error(String(r.status));
      const body = await r.json() as { groups: Group[] };
      if (mine !== seq) return; // a newer search has started
      records = body.groups;
    } catch (e) {
      if ((e as Error).name === 'AbortError' || mine !== seq) return;
      records = [];
    }
    loading = false; render();
  }

  function open(from?: HTMLElement | null) {
    if (dlg.open) return;
    opener = from ?? (document.activeElement as HTMLElement | null);
    document.body.removeAttribute('data-nav-open'); // the phone drawer is out of the way
    records = []; input.value = ''; loading = false;
    dlg.showModal();
    render();
    input.focus();
  }
  const close = () => { if (dlg.open) dlg.close(); };
  dlg.addEventListener('close', () => { ctl?.abort(); seq++; clearTimeout(timer); (opener && opener.isConnected ? opener : null)?.focus(); });
  dlg.addEventListener('click', e => { if (e.target === dlg) close(); }); // the dimmed area

  input.addEventListener('input', () => {
    render(); // pages filter at once; records follow after a short pause
    clearTimeout(timer);
    timer = setTimeout(fetchRecords, 150);
  });

  input.addEventListener('keydown', e => {
    if (e.isComposing || e.keyCode === 229) return; // an IME is choosing characters: Enter and arrows are its own
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setActive(active + (e.key === 'ArrowDown' ? 1 : -1)); }
    else if (e.key === 'Enter') {
      const cur = options()[active];
      e.preventDefault();
      if (!cur) return;
      if (e.metaKey || e.ctrlKey) window.open(cur.href, '_blank', 'noopener'); else location.assign(cur.href);
    }
  });
  list.addEventListener('mousemove', e => { const i = options().indexOf((e.target as HTMLElement).closest('[role=option]') as HTMLAnchorElement); if (i >= 0 && i !== active) setActive(i, false); });

  document.addEventListener('keydown', e => {
    if (e.isComposing || e.keyCode === 229) return;
    if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') { e.preventDefault(); if (dlg.open) close(); else open(); }
  });

  document.addEventListener('click', e => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-search-open]');
    if (!b || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return; // a new-tab click keeps the plain link
    e.preventDefault();
    open(b);
  });
}
